import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, X } from "lucide-react";

import { Button } from "../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";

/**
 * Multi-select checkbox dropdown for the Availability grid.
 *
 * A12.2: per-filter "clear selection" removed from inside each popover.
 * Only the page-level clear-all button remains, which keeps the filter
 * row visually calm.
 */
function MultiSelect({
  triggerLabel,
  placeholder,
  options,
  selected,
  onToggle,
  triggerTestId,
  contentTestId,
  optionTestIdPrefix,
}) {
  const [open, setOpen] = useState(false);
  const count = selected.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={triggerTestId}
          className="h-10 min-w-[180px] justify-between border-border-subtle bg-surface text-text-primary hover:bg-offwhite"
        >
          <span className="truncate">
            {count === 0 ? placeholder : `${triggerLabel} (${count})`}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 text-text-secondary" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-80 w-64 overflow-y-auto border-border-subtle bg-surface p-1"
        data-testid={contentTestId}
      >
        {options.length === 0 && (
          <div className="px-2 py-3 text-xs text-text-secondary">—</div>
        )}
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              role="menuitemcheckbox"
              aria-checked={active}
              onClick={() => onToggle(opt)}
              data-testid={`${optionTestIdPrefix}-${opt}`}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                active ? "bg-offwhite" : "hover:bg-offwhite"
              }`}
            >
              <span className="flex h-4 w-4 flex-none items-center justify-center rounded border border-border-subtle bg-surface">
                {active && <Check className="h-3 w-3 text-charcoal" />}
              </span>
              <span className="truncate">{opt}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export function AvailabilityFilters({
  categories,
  brands,
  selectedCategories,
  selectedBrands,
  onToggleCategory,
  onToggleBrand,
  onClear,
  isFiltered,
}) {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="availability-filters"
    >
      <MultiSelect
        triggerLabel={t("availability.filter_brands")}
        placeholder={t("availability.all_brands")}
        options={brands}
        selected={selectedBrands}
        onToggle={onToggleBrand}
        triggerTestId="availability-filter-brand-trigger"
        contentTestId="availability-filter-brand-content"
        optionTestIdPrefix="availability-filter-brand"
      />
      <MultiSelect
        triggerLabel={t("availability.filter_categories")}
        placeholder={t("availability.all_categories")}
        options={categories}
        selected={selectedCategories}
        onToggle={onToggleCategory}
        triggerTestId="availability-filter-category-trigger"
        contentTestId="availability-filter-category-content"
        optionTestIdPrefix="availability-filter-category"
      />

      {isFiltered && (
        <Button
          variant="ghost"
          onClick={onClear}
          data-testid="availability-filter-clear"
          className="h-10 text-taupe hover:bg-offwhite hover:text-charcoal"
        >
          <X className="mr-1 h-4 w-4" />
          {t("availability.clear_filters")}
        </Button>
      )}
    </div>
  );
}
